import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";import { FavoritesProvider } from "./context/FavoritesContext";
import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import RobotsMeta from "./components/RobotsMeta";

// Loading spinner for lazy loaded pages
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0f1d2f] border-t-transparent"></div>
  </div>
);

// Version checker - detects old cached versions and forces reload
function VersionChecker() {
  const checkForUpdates = useCallback(async () => {
    try {
      // Fetch the main HTML page with cache-busting
      const response = await fetch(`/?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      
      if (response.ok) {
        const html = await response.text();
        // Extract the main.js filename from the HTML (it contains a hash)
        const match = html.match(/main\.([a-f0-9]+)\.js/);
        if (match) {
          const serverHash = match[1];
          // Check against current script tags
          const scripts = document.querySelectorAll('script[src*="main."]');
          let clientHash = null;
          scripts.forEach(script => {
            const srcMatch = script.src.match(/main\.([a-f0-9]+)\.js/);
            if (srcMatch) clientHash = srcMatch[1];
          });
          
          if (clientHash && serverHash !== clientHash) {
            console.log(`Update detected: ${clientHash} → ${serverHash}. Reloading...`);
            // Clear caches
            if ('caches' in window) {
              const names = await caches.keys();
              await Promise.all(names.map(n => caches.delete(n)));
            }
            // Force reload from server
            window.location.href = window.location.href.split('?')[0] + '?_reload=' + Date.now();
          }
        }
      }
    } catch (err) {
      // Silent fail - don't interrupt user
    }
  }, []);

  useEffect(() => {
    // Check on mount
    checkForUpdates();
    
    // Check when user returns to tab
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    };
    
    // Check periodically (every 5 minutes)
    const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
    
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, [checkForUpdates]);

  return null;
}

// Scroll to top on navigation
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Core Pages - loaded immediately for fast initial render
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";

// Lazy loaded pages - loaded on demand
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const ForFarms = lazy(() => import("./pages/ForFarms"));
const ForCustomers = lazy(() => import("./pages/ForCustomers"));
const ApplyFarm = lazy(() => import("./pages/ApplyFarm"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Faq = lazy(() => import("./pages/Faq"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminFarmInvitations = lazy(() => import("./pages/AdminFarmInvitations"));
const AdminBrandCopy = lazy(() => import("./pages/AdminBrandCopy"));
const AdminFarmerOrderEmails = lazy(() => import("./pages/AdminFarmerOrderEmails"));
const AdminAccountTestTools = lazy(() => import("./pages/AdminAccountTestTools"));
const AdminMembershipPreview = lazy(() => import("./pages/AdminMembershipPreview"));
const Membership = lazy(() => import("./pages/Membership"));
const AdminFulfillmentAudit = lazy(() => import("./pages/AdminFulfillmentAudit"));
const AdminReferrals = lazy(() => import("./pages/AdminReferrals"));
const FarmerOrderDetail = lazy(() => import("./pages/FarmerOrderDetail"));
const FarmerPreview = lazy(() => import("./pages/FarmerPreview"));
const GettingStarted = lazy(() => import("./pages/GettingStarted"));
const Favorites = lazy(() => import("./pages/Favorites"));
const FarmSetup = lazy(() => import("./pages/FarmSetup"));

// Free exploration hub + feature pages (approved customer browsing additions)
const Explore = lazy(() => import("./pages/Explore"));
const GoogleAuthCallback = lazy(() => import("./pages/GoogleAuthCallback"));
const WelcomeDiscover = lazy(() => import("./pages/WelcomeDiscover"));
const DirectoryRequest = lazy(() => import("./pages/DirectoryRequest"));
const DirectoryDataNotices = lazy(() => import("./pages/DirectoryDataNotices"));
const SeasonalFieldGuide = lazy(() => import("./pages/SeasonalFieldGuide"));
const MyWishList = lazy(() => import("./pages/MyWishList"));
const FollowRegion = lazy(() => import("./pages/FollowRegion"));
const YourLarder = lazy(() => import("./pages/YourLarder"));
const RecipeBook = lazy(() => import("./pages/RecipeBook"));
const RecipeDetail = lazy(() => import("./pages/RecipeDetail"));
const FarmstandTV = lazy(() => import("./pages/FarmstandTV"));
const LiveCountryside = lazy(() => import("./pages/LiveCountryside"));
const ShoppingLists = lazy(() => import("./pages/ShoppingLists"));
const Outings = lazy(() => import("./pages/Outings"));
const Keepsakes = lazy(() => import("./pages/Keepsakes"));
const OfflinePage = lazy(() => import("./pages/Offline"));
const Experiences = lazy(() => import("./pages/Experiences"));
const ExperienceDetail = lazy(() => import("./pages/ExperienceDetail"));
const AdminFarmstandContent = lazy(() => import("./pages/AdminFarmstandContent"));
const AdminDiscoverContent = lazy(() => import("./pages/AdminDiscoverContent"));
const FarmStory = lazy(() => import("./pages/FarmStory"));
const FarmstandJournalList = lazy(() => import("./pages/FarmstandJournal").then(m => ({ default: m.FarmstandJournalList })));
const FarmstandJournalArticle = lazy(() => import("./pages/FarmstandJournal").then(m => ({ default: m.FarmstandJournalArticle })));

// Phase 2 Pages - lazy loaded
const Marketplace = lazy(() => import("./pages/Marketplace"));
const FarmDetail = lazy(() => import("./pages/FarmDetail"));
const MarketDetail = lazy(() => import("./pages/MarketDetail"));
const FarmMap = lazy(() => import("./pages/FarmMap"));
const Cart = lazy(() => import("./pages/Cart"));
const ConfirmEmail = lazy(() => import("./pages/ConfirmEmail"));
const FarmDashboard = lazy(() => import("./pages/FarmDashboard"));
const FarmProfileEdit = lazy(() => import("./pages/FarmProfileEdit"));
const FarmSetupReview = lazy(() => import("./pages/FarmSetupReview"));
const AdminFarmPrepareSetup = lazy(() => import("./pages/AdminFarmPrepareSetup"));
const AdminFarmProfile = lazy(() => import("./pages/AdminFarmProfile"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));

// Farm Onboarding Pages - lazy loaded
const FarmOnboarding = lazy(() => import("./pages/FarmOnboarding"));
const StripeComplete = lazy(() => import("./pages/StripeComplete"));

// Legal Pages - lazy loaded
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import { FarmstandShell } from "./components/farmstand/FarmstandShell";
import Friends, { FriendInvite, VerifyEmail } from "./pages/Friends";
import FriendProfile, { EditFriendProfile } from "./pages/FriendProfile";
import Messages, { Conversation, NewDirectMessage } from "./pages/Messages";
import { Journal, Spaces, Space, MemberRecipes, MemberRecipe } from "./pages/SharedContent";
import AdminSocialReports from "./pages/AdminSocialReports";
import AdminRoute from "./components/AdminRoute";
import Analytics from "./components/Analytics";
import ConsentBanner from "./components/ConsentBanner";
import NotFound from "./pages/NotFound";
import { AppErrorBoundary } from "./components/AppErrorBoundary";

import "@/App.css";

// Remount on query change so leaving Google link mode clears the prefilled form.
function LoginKeyed() {
  const { search } = useLocation();
  return <Login key={search} />;
}


function App() {
  return (
    <AuthProvider>
      <FavoritesProvider>
        <BrowserRouter>
          <VersionChecker />
          <ScrollToTop />
          <RobotsMeta />
          <Analytics />
          <div className="min-h-screen flex flex-col bg-warm-alabaster">
            <Header />
            <main className="flex-1">
              <AppErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/our-marketplace" element={<HowItWorks />} />
                  <Route path="/how-it-works" element={<Navigate to="/our-marketplace" replace />} />
                <Route path="/for-farms" element={<ForFarms />} />
                <Route path="/farmer/preview" element={<FarmerPreview />} />
                <Route path="/getting-started" element={<GettingStarted />} />
                <Route path="/for-farms/apply" element={<ApplyFarm />} />
                <Route path="/for-customers" element={<ForCustomers />} />
                <Route path="/apply" element={<ApplyFarm />} />
                <Route path="/subscribe" element={<Subscribe />} />
                <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/login" element={<LoginKeyed />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/auth/google" element={<GoogleAuthCallback />} />
                <Route path="/directory/request" element={<DirectoryRequest />} />
                <Route path="/welcome" element={
                  <ProtectedRoute>
                    <WelcomeDiscover />
                  </ProtectedRoute>
                } />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/account/confirm-email" element={<ConfirmEmail />} />
                <Route path="/farm-setup" element={<FarmSetup />} />
                <Route path="/membership" element={
                  <ProtectedRoute>
                    <FarmstandShell account><Membership /></FarmstandShell>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <FarmstandShell account><CustomerDashboard /></FarmstandShell>
                  </ProtectedRoute>
                } />
                
                {/* Phase 2: Marketplace Routes */}
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/marketplace/farms/:farmId" element={<FarmDetail />} />
                <Route path="/marketplace/markets/:marketId" element={<MarketDetail />} />
                <Route path="/marketplace/listings/:listingId" element={<MarketDetail />} />
                <Route path="/marketplace/map" element={<FarmMap />} />
                <Route path="/marketplace/stories/:storyId" element={<FarmStory />} />
                <Route path="/live-countryside" element={<ProtectedRoute><LiveCountryside /></ProtectedRoute>} />
                <Route path="/cart" element={<FarmstandShell account><Cart /></FarmstandShell>} />
                <Route path="/order/success" element={<OrderSuccess />} />
                <Route path="/orders" element={<FarmstandShell account><Orders /></FarmstandShell>} />
              <Route path="/orders/:orderId" element={<FarmstandShell account><OrderDetail /></FarmstandShell>} />
              <Route path="/favorites" element={<FarmstandShell account><Favorites /></FarmstandShell>} />
              
              {/* Phase 2: Farm Dashboard Routes */}
              <Route path="/farm-dashboard" element={<FarmDashboard />} />
              <Route path="/farm-dashboard/profile" element={<FarmProfileEdit />} />
              <Route path="/farm/dashboard" element={<FarmDashboard />} />
              <Route path="/farm/setup-review" element={
                <ProtectedRoute>
                  <FarmSetupReview />
                </ProtectedRoute>
              } />
              
              {/* Farm Onboarding Routes */}
              <Route path="/farm/onboarding" element={
                <ProtectedRoute>
                  <FarmOnboarding />
                </ProtectedRoute>
              } />
              {/* Legacy/alias path: /farmer/onboarding is referenced from
                  the Getting Started guide and the Farm Setup success toast.
                  Prior to this fix those links produced an empty page with
                  only header/footer because no matching route existed.
                  Redirecting into the canonical protected route preserves
                  the destination through the login flow.
                  (data-testid injected on the Navigate for regression test.) */}
              <Route path="/farmer/onboarding" element={
                <Navigate to="/farm/onboarding" replace />
              } />
              <Route path="/farm/stripe-complete" element={
                <ProtectedRoute>
                  <StripeComplete />
                </ProtectedRoute>
              } />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } />
              <Route path="/admin/analytics" element={
                <AdminRoute>
                  <AdminAnalytics />
                </AdminRoute>
              } />
              <Route path="/admin/invitations" element={
                <AdminRoute>
                  <AdminFarmInvitations />
                </AdminRoute>
              } />
              <Route path="/admin/outreach" element={
                <AdminRoute>
                  <AdminFarmInvitations />
                </AdminRoute>
              } />
              <Route path="/admin/farms/:farmId/profile" element={
                <AdminRoute>
                  <AdminFarmProfile />
                </AdminRoute>
              } />
              <Route path="/admin/farms/:farmId/prepare-setup" element={
                <AdminRoute>
                  <AdminFarmPrepareSetup />
                </AdminRoute>
              } />
              <Route path="/admin/brand-copy" element={
                <AdminRoute>
                  <AdminBrandCopy />
                </AdminRoute>
              } />
              <Route path="/admin/order-emails" element={
                <AdminRoute>
                  <AdminFarmerOrderEmails />
                </AdminRoute>
              } />
              <Route path="/admin/account-tools" element={<AdminRoute><AdminAccountTestTools /></AdminRoute>} />
              <Route path="/admin/membership-preview" element={<AdminRoute><AdminMembershipPreview /></AdminRoute>} />
              <Route path="/admin/referrals" element={
                <AdminRoute>
                  <AdminReferrals />
                </AdminRoute>
              } />
              <Route path="/admin/fulfillment-audit" element={
                <AdminRoute>
                  <AdminFulfillmentAudit />
                </AdminRoute>
              } />
              <Route path="/farm-dashboard/orders/:orderId" element={
                <FarmerOrderDetail />
              } />
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } />

              {/* Legal Pages */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/directory-data-notices" element={<DirectoryDataNotices />} />

              {/* Free exploration hub — account-only (free + paid customers) */}
              <Route path="/explore" element={
                <ProtectedRoute>
                  <Explore />
                </ProtectedRoute>
              } />
              <Route path="/explore/seasonal-guide" element={
                <ProtectedRoute>
                  <FarmstandShell legacy testid="legacy-shell-seasonal"><SeasonalFieldGuide /></FarmstandShell>
                </ProtectedRoute>
              } />
              <Route path="/explore/wish-list" element={
                <ProtectedRoute>
                  <FarmstandShell legacy testid="legacy-shell-wishlist"><MyWishList /></FarmstandShell>
                </ProtectedRoute>
              } />
              <Route path="/explore/region" element={
                <ProtectedRoute>
                  <FarmstandShell legacy testid="legacy-shell-region"><FollowRegion /></FarmstandShell>
                </ProtectedRoute>
              } />
              <Route path="/explore/larder" element={<ProtectedRoute><YourLarder /></ProtectedRoute>} />
              <Route path="/explore/lists" element={<ProtectedRoute><ShoppingLists /></ProtectedRoute>} />
              <Route path="/explore/outings" element={<ProtectedRoute><Outings /></ProtectedRoute>} />
              <Route path="/explore/keepsakes" element={<ProtectedRoute><Keepsakes /></ProtectedRoute>} />
              <Route path="/offline" element={<OfflinePage />} />
              <Route path="/explore/outings/:oid" element={<ProtectedRoute><Outings /></ProtectedRoute>} />
              <Route path="/explore/lists/:lid" element={<ProtectedRoute><ShoppingLists /></ProtectedRoute>} />
              <Route path="/explore/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
              <Route path="/explore/friends/profile" element={<ProtectedRoute><EditFriendProfile /></ProtectedRoute>} />
              <Route path="/explore/friends/:uid" element={<ProtectedRoute><FriendProfile /></ProtectedRoute>} />
              <Route path="/explore/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
              <Route path="/explore/messages/new/:uid" element={<ProtectedRoute><NewDirectMessage /></ProtectedRoute>} />
              <Route path="/explore/messages/:threadId" element={<ProtectedRoute><Conversation /></ProtectedRoute>} />
              <Route path="/explore/photo-journal" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
              <Route path="/explore/shared" element={<ProtectedRoute><Spaces /></ProtectedRoute>} />
              <Route path="/explore/shared/:sid" element={<ProtectedRoute><Space /></ProtectedRoute>} />
              <Route path="/explore/member-recipes" element={<ProtectedRoute><MemberRecipes /></ProtectedRoute>} />
              <Route path="/explore/member-recipes/:rid" element={<ProtectedRoute><MemberRecipe /></ProtectedRoute>} />
              <Route path="/friends/invite" element={<FriendInvite />} />
              <Route path="/account/verify-email" element={<VerifyEmail />} />
              <Route path="/admin/social-reports" element={<AdminRoute><AdminSocialReports /></AdminRoute>} />
              <Route path="/explore/recipes" element={<ProtectedRoute><RecipeBook /></ProtectedRoute>} />
              <Route path="/explore/recipes/:recipeId" element={<ProtectedRoute><RecipeDetail /></ProtectedRoute>} />
              <Route path="/explore/tv" element={<ProtectedRoute><FarmstandTV /></ProtectedRoute>} />
              <Route path="/explore/tv/:videoId" element={<ProtectedRoute><FarmstandTV /></ProtectedRoute>} />
              <Route path="/explore/experiences" element={<ProtectedRoute><Experiences /></ProtectedRoute>} />
              <Route path="/explore/experiences/:eid" element={<ProtectedRoute><ExperienceDetail /></ProtectedRoute>} />
              <Route path="/admin/farmstand-content" element={<AdminRoute><AdminFarmstandContent /></AdminRoute>} />
              <Route path="/admin/discover-content" element={<AdminRoute><AdminDiscoverContent /></AdminRoute>} />
              <Route path="/explore/journal" element={
                <ProtectedRoute>
                  <FarmstandShell legacy testid="legacy-shell-journal"><FarmstandJournalList /></FarmstandShell>
                </ProtectedRoute>
              } />
              <Route path="/explore/journal/:slug" element={
                <ProtectedRoute>
                  <FarmstandShell legacy testid="legacy-shell-article"><FarmstandJournalArticle /></FarmstandShell>
                </ProtectedRoute>
              } />
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
              </AppErrorBoundary>
          </main>
          <Footer />
          <ConsentBanner />
        </div>
        <Toaster position="top-right" />
      </BrowserRouter>
    </FavoritesProvider>
  </AuthProvider>
  );
}

export default App;
